import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';
import { useTheme } from './ThemeContext';

// Context for modal state
const ModalContext = createContext(null);

// Hook to use modal anywhere in app
export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within ModalProvider');
  }
  return context;
}

// Provider component
export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);

  const showAlert = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setModal({
        type: 'alert',
        message,
        title: options.title || 'Notice',
        icon: options.icon || 'info',
        buttonText: options.buttonText || 'OK',
        onClose: () => {
          setModal(null);
          resolve();
        }
      });
    });
  }, []);

  const showConfirm = useCallback((message, options = {}) => {
    return new Promise((resolve) => {
      setModal({
        type: 'confirm',
        message,
        title: options.title || 'Confirm',
        icon: options.icon || 'warning',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        confirmStyle: options.confirmStyle || 'primary', // 'primary', 'danger'
        onConfirm: () => {
          setModal(null);
          resolve(true);
        },
        onCancel: () => {
          setModal(null);
          resolve(false);
        }
      });
    });
  }, []);

  const showSuccess = useCallback((message, options = {}) => {
    return showAlert(message, { ...options, icon: 'success', title: options.title || 'Success' });
  }, [showAlert]);

  const showError = useCallback((message, options = {}) => {
    return showAlert(message, { ...options, icon: 'error', title: options.title || 'Error' });
  }, [showAlert]);

  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  return (
    <ModalContext.Provider value={{ showAlert, showConfirm, showSuccess, showError, closeModal }}>
      {children}
      {modal && <Modal modal={modal} />}
    </ModalContext.Provider>
  );
}

// Modal component
function Modal({ modal }) {
  const { colors } = useTheme();

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (modal.type === 'confirm') {
          modal.onCancel?.();
        } else {
          modal.onClose?.();
        }
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [modal]);

  const getIcon = () => {
    switch (modal.icon) {
      case 'success':
        return <CheckCircle size={28} color={colors.success} />;
      case 'error':
        return <AlertCircle size={28} color={colors.error} />;
      case 'warning':
        return <AlertTriangle size={28} color={colors.warning} />;
      case 'info':
      default:
        return <Info size={28} color={colors.info} />;
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      if (modal.type === 'confirm') {
        modal.onCancel?.();
      } else {
        modal.onClose?.();
      }
    }
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100000,
        padding: '1rem',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideIn {
          from { transform: scale(0.95) translateY(-10px); opacity: 0; }
          to { transform: scale(1) translateY(0); opacity: 1; }
        }
      `}</style>

      <div style={{
        background: colors.bgCard,
        borderRadius: '12px',
        maxWidth: '400px',
        width: '100%',
        border: `1px solid ${colors.border}`,
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        animation: 'slideIn 0.2s ease-out'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1rem 1.25rem',
          borderBottom: `1px solid ${colors.bgInput}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {getIcon()}
            <h3 style={{
              margin: 0,
              color: colors.text,
              fontSize: '1.1rem',
              fontWeight: 600
            }}>
              {modal.title}
            </h3>
          </div>
          <button
            onClick={() => modal.type === 'confirm' ? modal.onCancel?.() : modal.onClose?.()}
            style={{
              background: 'none',
              border: 'none',
              color: colors.textMuted,
              cursor: 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.color = colors.text}
            onMouseOut={(e) => e.currentTarget.style.color = colors.textMuted}
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '1.25rem',
          color: '#ccc',
          fontSize: '0.95rem',
          lineHeight: '1.6',
          whiteSpace: 'pre-wrap'
        }}>
          {modal.message}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '0.75rem',
          padding: '1rem 1.25rem',
          borderTop: `1px solid ${colors.bgInput}`
        }}>
          {modal.type === 'confirm' ? (
            <>
              <button
                onClick={modal.onCancel}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: colors.bgInput,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#ccc',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = colors.border}
                onMouseOut={(e) => e.currentTarget.style.background = colors.bgInput}
              >
                {modal.cancelText}
              </button>
              <button
                onClick={modal.onConfirm}
                style={{
                  padding: '0.625rem 1.25rem',
                  background: modal.confirmStyle === 'danger' ? colors.error : colors.accent,
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
              >
                {modal.confirmText}
              </button>
            </>
          ) : (
            <button
              onClick={modal.onClose}
              style={{
                padding: '0.625rem 1.5rem',
                background: colors.accent,
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '0.9rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              {modal.buttonText}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ModalProvider;
